// ABOUTME: Reachability engine for path-predictor mode (claim a set, see what stays achievable).
// ABOUTME: A build is valid iff its total affinity covers every member's requirement AND it is
// ABOUTME: constructible from the refundable crossroads seed (some order places each member with
// ABOUTME: its requirement met). minCost is bracketed by a fast cover-table lower bound (sound for
// ABOUTME: "dim") and a constructibility-aware greedy upper bound (sound for "reachable").
import { AFFINITIES, type DevotionModel, type StarId } from "./types";
import { gateBuildOrder, gateTransition, type StepState, type TransStep } from "./orderLegality";
import { transitionOrderPath, type TransitionRung } from "./transitionOrder";

export type Vec = [number, number, number, number, number]; // order = AFFINITIES
// Hard per-color cap: the max requirement that gates anything; affinity beyond this is worthless.
const CAP_MAX: Vec = [20, 8, 20, 10, 20];
const NOCOST = 65535;
export const INF = 1e9;
export const BUDGET = 55;
/**
 * The color of a crossroads-like constellation (one star, no requirement, a single +1 of one color), or -1.
 * A crossroads is refundable, so a build may hold one transiently while a member activates: the engine's
 * "seed". A seed is honest only for a crossroads the build is not already counting - one placed as a
 * member or as filler already contributes its +1 - so every seed below is computed against what is
 * placed, never assumed. Structural, so a synthetic model's crossroads are recognized too.
 */
export function crossroadsColor(c: ReachCon): number {
  if (c.size !== 1 || c.req[0] || c.req[1] || c.req[2] || c.req[3] || c.req[4]) return -1;
  let color = -1;
  for (let k = 0; k < 5; k++) {
    if (c.grant[k] === 0) continue;
    if (c.grant[k] !== 1 || color >= 0) return -1;
    color = k;
  }
  return color;
}

/** The transient seed a build may draw on: +1 per color with a crossroads-like constellation not in `counted`. */
function seedFor(cons: ReachCon[], counted: (c: ReachCon) => boolean): Vec {
  const seed = zero();
  for (const c of cons) {
    const k = crossroadsColor(c);
    if (k >= 0 && !counted(c)) seed[k] = 1;
  }
  return seed;
}
// Peak-witness search bounds (see classifyForSelection): it samples this many construction orders, each
// with a per-scaffold node cap, so it stays cheap even across a full sweep. The witness is self-bounding -
// it returns instantly for a non-self-covering build (no order can help) and only samples for a complete
// self-covering one - so it needs no budget-proximity gate; a sampler that finds no order keeps the dim.
const PEAK_WITNESS_TRIES = 32;
const PEAK_NODE_CAP = 3000;
// The resolver-gate witness uses the deterministic candidate orders only (no random shuffles): it keeps the
// gate cheap and, crucially, RNG-free so the Rust/WASM port stays bit-for-bit verdict-equivalent. Builds
// that need a shuffled order to fit budget stay conservatively dim (sound).
const GATE_WITNESS_TRIES = 0;

/** A constellation reduced to what reachability needs: its cost and affinity vectors. */
export interface ReachCon {
  id: string;
  size: number;
  req: Vec;
  grant: Vec;
}
/** A cover table carries its own grid dimensions (sized to the model's max requirements). */
export interface CoverTable {
  cost: Uint16Array;
  caps: Vec;
  strides: Vec;
}

const zero = (): Vec => [0, 0, 0, 0, 0];
const covers = (g: Vec, d: Vec): boolean =>
  g[0] >= d[0] && g[1] >= d[1] && g[2] >= d[2] && g[3] >= d[3] && g[4] >= d[4];
const addCap = (g: Vec, x: Vec): Vec => [
  Math.min(g[0] + x[0], CAP_MAX[0]!),
  Math.min(g[1] + x[1], CAP_MAX[1]!),
  Math.min(g[2] + x[2], CAP_MAX[2]!),
  Math.min(g[3] + x[3], CAP_MAX[3]!),
  Math.min(g[4] + x[4], CAP_MAX[4]!),
];
const addV = (g: Vec, x: Vec): Vec => [g[0] + x[0], g[1] + x[1], g[2] + x[2], g[3] + x[3], g[4] + x[4]];
const maxV = (a: Vec, b: Vec): Vec => [
  Math.max(a[0], b[0]),
  Math.max(a[1], b[1]),
  Math.max(a[2], b[2]),
  Math.max(a[3], b[3]),
  Math.max(a[4], b[4]),
];

function vecOf(m: Partial<Record<(typeof AFFINITIES)[number], number>>): Vec {
  return AFFINITIES.map((a) => m[a] ?? 0) as Vec;
}

/** Reduce a DevotionModel to the compact per-constellation data the search needs. */
export function buildReachCons(model: DevotionModel): ReachCon[] {
  const out: ReachCon[] = [];
  for (const c of model.constellations.values()) {
    out.push({ id: c.id, size: c.starIds.length, req: vecOf(c.affinityRequired), grant: vecOf(c.affinityBonus) });
  }
  return out;
}

/** The cover grid dimensions (caps + strides) for a model, without building the cost table. */
export function coverDims(cons: ReachCon[]): { caps: Vec; strides: Vec } {
  const caps: Vec = zero();
  for (const c of cons) for (let i = 0; i < 5; i++) caps[i] = Math.max(caps[i]!, c.req[i]!);
  for (let i = 0; i < 5; i++) caps[i] = Math.min(caps[i]!, CAP_MAX[i]!);
  const sizes = caps.map((c) => c + 1);
  const strides = sizes.map((_, i) => sizes.slice(i + 1).reduce((a, b) => a * b, 1)) as Vec;
  return { caps, strides };
}

/**
 * The crossroads-refund cover table. `cost[D]` = the minimum stars of a SUBSET of distinct
 * constellations whose summed (capped) affinity reaches at least `D`. Orderability-free: because
 * bootstraps are refundable, the cheapest way to reach an affinity vector is just the cheapest
 * subset that sums to it. Built once with an in-place 0/1 knapsack DP (descending so no
 * constellation is reused), then a 5D suffix-min so a lookup answers ">= D".
 *
 * The grid is capped per color at the model's maximum requirement (affinity beyond what anything
 * requires is useless), so a small model yields a tiny grid and the real model the full one.
 *
 * This is a LOWER BOUND on the true minCost (it ignores that filler must also be self-sustaining),
 * so it is sound for dimming: if `coverLowerBound > 55`, the claim is genuinely unreachable.
 */
export function buildCoverTable(cons: ReachCon[]): CoverTable {
  const { caps, strides } = coverDims(cons);
  const sizes = caps.map((c) => c + 1);
  const maxKey = sizes.reduce((a, b) => a * b, 1);
  const cost = new Uint16Array(maxKey).fill(NOCOST);
  cost[0] = 0;
  for (const c of cons) {
    if (!(c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4])) continue;
    const [g0, g1, g2, g3, g4] = c.grant;
    for (let a = caps[0]!; a >= 0; a--)
      for (let ch = caps[1]!; ch >= 0; ch--)
        for (let e = caps[2]!; e >= 0; e--)
          for (let o = caps[3]!; o >= 0; o--)
            for (let p = caps[4]!; p >= 0; p--) {
              const k = a * strides[0]! + ch * strides[1]! + e * strides[2]! + o * strides[3]! + p;
              const pc = cost[k]!;
              if (pc === NOCOST) continue;
              const nc = pc + c.size;
              if (nc > BUDGET) continue;
              const nk =
                Math.min(a + g0, caps[0]!) * strides[0]! +
                Math.min(ch + g1, caps[1]!) * strides[1]! +
                Math.min(e + g2, caps[2]!) * strides[2]! +
                Math.min(o + g3, caps[3]!) * strides[3]! +
                Math.min(p + g4, caps[4]!);
              if (nc < cost[nk]!) cost[nk] = nc;
            }
  }
  for (let i = 0; i < 5; i++) {
    const st = strides[i]!;
    for (let k = maxKey - 1; k >= 0; k--)
      if (Math.floor(k / st) % sizes[i]! < caps[i]!) {
        const up = cost[k + st]!;
        if (up < cost[k]!) cost[k] = up;
      }
  }
  return { cost, caps, strides };
}

function claimSummary(claimed: ReachCon[]) {
  let req = zero(),
    grant = zero(),
    grantUncapped = zero(),
    own = 0;
  for (const c of claimed) {
    req = maxV(req, c.req);
    grant = addCap(grant, c.grant);
    grantUncapped = addV(grantUncapped, c.grant);
    own += c.size;
  }
  return { req, grant, grantUncapped, own };
}

/** A normalized start point for the minCost bracket, derived from a raw star selection. */
export interface ReachState {
  own: number; // total selected stars (all count against budget); equals sum of `built` sizes
  supply: Vec; // affinity from COMPLETED constellations only, capped at CAP_MAX (cover-table indexing)
  supplyUncapped: Vec; // the same sum uncapped: the true in-game total (what displays show)
  target: Vec; // elementwise-max requirement over STARTED constellations
  startedIds: Set<string>;
  partialFinish: { id: string; remaining: number; grant: Vec; req: Vec }[];
  // The started constellations as already-committed members, for the constructibility check:
  // a completed one carries its full size and grant; a partial carries its SELECTED star count
  // and a zero grant (it imposes its requirement but supplies nothing until finished).
  built: ReachCon[];
}

/** Reduce a raw star selection to the data the bracket needs (honest partials). */
export function selectionSummary(model: DevotionModel, selected: Set<string>): ReachState {
  const selByCon = new Map<string, number>();
  let own = 0;
  for (const sid of selected) {
    const star = model.stars.get(sid);
    if (!star) continue;
    own++;
    selByCon.set(star.constellationId, (selByCon.get(star.constellationId) ?? 0) + 1);
  }
  let supply = zero(),
    supplyUncapped = zero(),
    target = zero();
  const startedIds = new Set<string>();
  const partialFinish: ReachState["partialFinish"] = [];
  const built: ReachCon[] = [];
  for (const [conId, count] of selByCon) {
    const c = model.constellations.get(conId);
    if (!c) continue;
    startedIds.add(conId);
    const req = vecOf(c.affinityRequired);
    target = maxV(target, req);
    const grant = vecOf(c.affinityBonus);
    if (count >= c.starIds.length) {
      supply = addCap(supply, grant);
      supplyUncapped = addV(supplyUncapped, grant);
      built.push({ id: conId, size: c.starIds.length, req, grant });
    } else {
      built.push({ id: conId, size: count, req, grant: zero() });
      if (grant[0] || grant[1] || grant[2] || grant[3] || grant[4])
        partialFinish.push({ id: conId, remaining: c.starIds.length - count, grant, req });
    }
  }
  return { own, supply, supplyUncapped, target, startedIds, partialFinish, built };
}

/** The unselected path to a star: the star plus every unselected transitive predecessor. Predecessors
 *  never cross constellations, so the path stays within the star's own constellation. This is the set
 *  a click on the star must add, and its size is the star's point cost from the current selection. */
export function pathToStar(model: DevotionModel, selected: Set<StarId>, starId: StarId): Set<StarId> {
  const path = new Set<StarId>();
  const stack: StarId[] = [starId];
  while (stack.length) {
    const id = stack.pop()!;
    if (path.has(id) || selected.has(id)) continue;
    path.add(id);
    const star = model.stars.get(id);
    if (star) for (const p of star.predecessors) stack.push(p);
  }
  return path;
}

/** Treat a fully-claimed set as a selection state: every claim is a completed member. */
export function stateFromClaimed(claimed: ReachCon[]): ReachState {
  const { req, grant, grantUncapped, own } = claimSummary(claimed);
  return {
    own,
    supply: grant,
    supplyUncapped: grantUncapped,
    target: req,
    startedIds: new Set(claimed.map((c) => c.id)),
    partialFinish: [],
    built: claimed.map((c) => ({ id: c.id, size: c.size, req: c.req, grant: c.grant })),
  };
}

/**
 * Lower bound on minCost for a selection state: own stars plus the cheapest filler to cover the
 * remaining affinity deficit. Partial finishes are credited as cheap completions - we minimise
 * over every subset of finishes (paying their stars, adding their grant to supply) so the cover
 * table only has to close whatever deficit the chosen finishes leave. This is a pure lower bound
 * (the cover table itself is one) and ignores constructibility, so it is sound for "dim".
 */
export function lowerBoundFrom(table: CoverTable, st: ReachState): number {
  const fins = st.partialFinish;
  let best = INF;
  for (let mask = 0; mask < 1 << fins.length; mask++) {
    let supply = st.supply,
      extra = 0;
    for (let i = 0; i < fins.length; i++)
      if (mask & (1 << i)) {
        supply = addCap(supply, fins[i]!.grant);
        extra += fins[i]!.remaining;
      }
    const deficit: Vec = [
      Math.max(0, st.target[0] - supply[0]),
      Math.max(0, st.target[1] - supply[1]),
      Math.max(0, st.target[2] - supply[2]),
      Math.max(0, st.target[3] - supply[3]),
      Math.max(0, st.target[4] - supply[4]),
    ];
    const cov = coverCostAt(table, deficit);
    if (cov >= INF) continue; // uncoverable for this subset; the INF must not be added to own
    const bound = st.own + extra + cov;
    if (bound < best) best = bound;
  }
  return best;
}

/** Lower bound on minCost(claimed): own stars + cheapest filler to cover the claimed deficit. */
export function coverLowerBound(table: CoverTable, claimed: ReachCon[]): number {
  return lowerBoundFrom(table, stateFromClaimed(claimed));
}

/** The filler a selection state may draw on: unstarted granting constellations plus its finishes. */
function fillerFor(cons: ReachCon[], st: ReachState): ReachCon[] {
  const filler = cons.filter(
    (c) => !st.startedIds.has(c.id) && (c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4]),
  );
  for (const p of st.partialFinish)
    filler.push({ id: `${p.id}#finish`, size: p.remaining, req: p.req, grant: p.grant });
  return filler;
}

/**
 * Refund-aware greedy: construct a valid build placing every claimed constellation, seeded by
 * the crossroads it has not placed (seedFor), repeatedly adding the unlocked constellation that best
 * closes the affinity deficit per star. Once the claimed are placed and the build's own affinity covers every placed
 * member's requirement, the crossroads are refunded and the cost excludes them.
 *
 * Returns the post-refund STEADY-STATE cost, which is a lower bound on the construction PEAK (you end
 * holding every permanent member). So `cost <= budget` does NOT by itself prove reachability for tight
 * budgets - the transient crossroads peak can still overflow. Reachability is decided by
 * peakGateReachable / the peak witness, which charge that peak; greedy serves only as a fast lower bound.
 */
export function greedyMinCost(cons: ReachCon[], claimedIds: string[], budget = BUDGET): number {
  const byId = new Map(cons.map((c) => [c.id, c]));
  const claimed = claimedIds.map((id) => byId.get(id)!);
  return greedyFrom(cons, stateFromClaimed(claimed), budget);
}

/**
 * Refund-aware greedy for a selection state. Auto-places every already-committed member
 * (`st.built`, partials included as zero-grant) in seed-unlock order, then adds filler (unstarted
 * granting constellations and partial finishes) by best deficit-reduction-per-star, until every
 * committed member is placed AND the build's own affinity covers every placed requirement.
 * Returns the post-refund steady-state cost: a lower bound on the construction PEAK, not a sound
 * reachable proof for tight budgets (it ignores the transient crossroads held to bootstrap affinity).
 * peakGateReachable / the peak witness decide reachability; greedy is only a fast lower bound.
 */
// Distinct crossroads colors the LAST greedyFrom run had to bootstrap (a placement needed the seed's +1
// for that color). Holding one crossroads per such color realizes greedy's order, so its construction peak
// is greedyCost + this count - a sound, tight upper bound (Ted's ladder: affinity persists, so each color
// is a one-time bottom-of-ladder cost). Read immediately after greedyFrom; only meaningful on a finite cost.
let greedyBootColors = 0;
/** Distinct crossroads colors bootstrapped by the last greedyFrom run (see greedyBootColors). */
export function lastGreedyBootColors(): number {
  return greedyBootColors;
}
const popcount5 = (m: number): number => (m & 1) + ((m >> 1) & 1) + ((m >> 2) & 1) + ((m >> 3) & 1) + ((m >> 4) & 1);

export function greedyFrom(cons: ReachCon[], st: ReachState, budget = BUDGET): number {
  greedyBootColors = 0;
  const built = st.built;
  const pool = [...built, ...fillerFor(cons, st)];
  const placed = new Array(pool.length).fill(false);
  // Every crossroads-like constellation is in the pool (started ones in built, the rest as filler); the
  // seed offers a color only while one of its crossroads is unplaced, so a crossroads is never counted
  // twice (once as the transient seed, again as a placed member or filler).
  const seedLeft = zero();
  for (const c of pool) {
    const k = crossroadsColor(c);
    if (k >= 0) seedLeft[k]!++;
  }
  const seed = (): Vec => seedLeft.map((n) => (n > 0 ? 1 : 0)) as Vec;
  const place = (i: number): void => {
    placed[i] = true;
    const k = crossroadsColor(pool[i]!);
    if (k >= 0) seedLeft[k]!--;
  };
  let build = zero(); // affinity from placed constellations (excludes the transient seed)
  let maxReqPlaced = zero(); // every placed constellation must stand under this once seed is gone
  let cost = 0,
    builtLeft = built.length;
  let bootMask = 0; // colors any placement needed the seed crossroads for (req[c] > build[c] at placement)
  for (;;) {
    let did = false;
    // Auto-place the committed members as they unlock; filler is added selectively below.
    for (let i = 0; i < built.length; i++) {
      if (placed[i] || !covers(addCap(seed(), build), built[i]!.req)) continue;
      for (let c = 0; c < 5; c++) if (built[i]!.req[c]! > build[c]!) bootMask |= 1 << c;
      place(i);
      cost += built[i]!.size;
      build = addCap(build, built[i]!.grant);
      maxReqPlaced = maxV(maxReqPlaced, built[i]!.req);
      builtLeft--;
      did = true;
    }
    if (builtLeft === 0 && covers(build, maxReqPlaced)) {
      greedyBootColors = popcount5(bootMask);
      return cost <= budget ? cost : INF;
    }
    if (did) continue;
    const g2 = addCap(seed(), build);
    const target = covers(build, maxReqPlaced) ? st.target : maxReqPlaced; // close self-sustain first, then started reqs
    const deficit: Vec = [
      Math.max(0, target[0]! - build[0]),
      Math.max(0, target[1]! - build[1]),
      Math.max(0, target[2]! - build[2]),
      Math.max(0, target[3]! - build[3]),
      Math.max(0, target[4]! - build[4]),
    ];
    let best = -1,
      bestScore = 0;
    for (let i = built.length; i < pool.length; i++) {
      if (placed[i] || !covers(g2, pool[i]!.req)) continue;
      let red = 0;
      for (let j = 0; j < 5; j++) red += Math.min(pool[i]!.grant[j]!, deficit[j]!);
      const score = red / pool[i]!.size;
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (best < 0 || bestScore === 0) return INF;
    for (let c = 0; c < 5; c++) if (pool[best]!.req[c]! > build[c]!) bootMask |= 1 << c;
    place(best);
    cost += pool[best]!.size;
    build = addCap(build, pool[best]!.grant);
    maxReqPlaced = maxV(maxReqPlaced, pool[best]!.req);
    if (cost > budget) return INF;
  }
}

export type Reach = "reachable" | "dim" | "unknown";

/**
 * Classify a candidate claim by bracketing minCost. The cover lower bound soundly proves "dim";
 * the peak gate soundly proves "reachable". The (rare) gap between is "unknown".
 */
export function classify(cons: ReachCon[], table: CoverTable, claimedIds: string[], budget = BUDGET): Reach {
  const byId = new Map(cons.map((c) => [c.id, c]));
  const claimed = claimedIds.map((id) => byId.get(id)!);
  if (coverLowerBound(table, claimed) > budget) return "dim";
  // Sound reachable proof that charges the transient crossroads peak (greedyMinCost's refunded cost did
  // not, so it false-reached tight builds). The tight band it cannot decide stays "unknown" for the
  // exact resolver, which models the peak.
  if (peakGateReachable(cons, claimed, budget)) return "reachable";
  return "unknown";
}

/** For a current claimed set S, classify every other constellation as a candidate next claim. */
export function reachabilitySweep(
  cons: ReachCon[],
  table: CoverTable,
  claimedIds: string[],
  budget = BUDGET,
): Map<string, Reach> {
  const out = new Map<string, Reach>();
  const claimedSet = new Set(claimedIds);
  for (const c of cons) {
    if (claimedSet.has(c.id)) continue;
    out.set(c.id, classify(cons, table, [...claimedIds, c.id], budget));
  }
  return out;
}

function coverCostAt(table: CoverTable, deficit: Vec): number {
  let k = 0;
  for (let i = 0; i < 5; i++) k += Math.min(Math.max(0, deficit[i]!), table.caps[i]!) * table.strides[i]!;
  const v = table.cost[k]!;
  return v === NOCOST ? INF : v;
}

/** Can every member of B be completed in some order, seeded by the crossroads B does not already contain? */
function constructible(cons: ReachCon[], B: ReachCon[]): boolean {
  const inB = new Set(B.map((m) => m.id));
  let gain = seedFor(cons, (c) => inB.has(c.id));
  const done = B.map(() => false);
  let placed = 0,
    changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < B.length; i++) {
      if (done[i] || !covers(gain, B[i]!.req)) continue;
      done[i] = true;
      placed++;
      gain = addCap(gain, B[i]!.grant);
      changed = true;
    }
  }
  return placed === B.length;
}

/**
 * Cheap SOUND sufficient proof that build `B` is reachable within `budget`, accounting for the
 * transient construction peak that `constructible`/greedy ignore. Affinity persists, so each distinct
 * required color needs at most one refundable crossroads held at once; a no-refund schedule that places
 * one crossroads per required color and then the whole self-covering build peaks at
 * `size + distinctRequiredColors`. So if B is self-covering AND seed-constructible (an order
 * exists) AND `size + distinctRequiredColors <= budget`, a genuine construction order fits the budget.
 *
 * Sound: it never accepts an unbuildable build (measured 0 wrong-accepts across ~18k near-ceiling
 * builds). NOT complete: the tight band `size < budget < size + distinctRequiredColors` - where colors
 * bootstrap sequentially so not every crossroads is held at once - returns false and must fall through
 * to the peak witness. Crucially RNG-free, so the WASM resolver port stays verdict-equivalent.
 */
function peakGateReachable(cons: ReachCon[], B: ReachCon[], budget: number): boolean {
  let size = 0;
  let grant = zero(),
    req = zero();
  for (const m of B) {
    size += m.size;
    grant = addCap(grant, m.grant);
    req = maxV(req, m.req);
  }
  if (!covers(grant, req)) return false; // not self-covering: needs permanent support, this gate cannot decide it
  let colors = 0;
  for (let i = 0; i < 5; i++) if (req[i]! > 0) colors++;
  if (size + colors > budget) return false; // peak may exceed budget; defer to the witness
  return constructible(cons, B); // a seeded order must exist, else the peak bound is not valid
}

/**
 * Minimum transient construction peak (points held at once) to stand up refundable scaffolding that
 * grants at least `deficit` affinity, charging each scaffold's own bootstrap. Crossroads (requirement
 * zero) are the floor. Reaching eldritch 3 via Quill (4 stars, itself needing eldritch 1) means
 * holding the eldritch crossroads while Quill is placed, so the peak is 5, not 4.
 *
 * Equals the smallest bootstrap-closed, constructible scaffold set whose grants cover `deficit`: a
 * no-refund schedule holds that set monotonically, so its peak is its size. That is a sound UPPER
 * bound on the true min-peak (refunding a now-redundant bootstrap can only lower it), so it never
 * under-charges, which is the safe direction for dimming. Returns INF if `deficit` is uncoverable.
 *
 * Implemented as a cover-table-pruned DFS over distinct scaffolds (a used-set forbids reusing a
 * one-of-a-kind scaffold, e.g. a single crossroads, which would fabricate affinity that does not
 * exist). Scaffolds are tried high-grant-per-star first so a tight bound is found early and prunes.
 */
export function peakToReach(
  cons: ReachCon[],
  table: CoverTable,
  deficit: Vec,
  base: Vec = [0, 0, 0, 0, 0],
  peakNodeCap = 300_000,
  opts?: { collect?: ReachCon[]; preferSmall?: boolean },
): number {
  const need: Vec = [
    Math.max(0, deficit[0]),
    Math.max(0, deficit[1]),
    Math.max(0, deficit[2]),
    Math.max(0, deficit[3]),
    Math.max(0, deficit[4]),
  ];
  if (need[0] === 0 && need[1] === 0 && need[2] === 0 && need[3] === 0 && need[4] === 0) {
    if (opts?.collect) opts.collect.length = 0;
    return 0;
  }
  const grants = (c: ReachCon) => c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4];
  const ratio = (c: ReachCon) => (c.grant[0] + c.grant[1] + c.grant[2] + c.grant[3] + c.grant[4]) / c.size;
  const scaffolds = cons.filter(grants).sort(
    opts?.preferSmall
      ? (a, b) => {
          const reqFree = (c: ReachCon) =>
            c.req[0] === 0 && c.req[1] === 0 && c.req[2] === 0 && c.req[3] === 0 && c.req[4] === 0;
          return (reqFree(b) ? 1 : 0) - (reqFree(a) ? 1 : 0) || a.size - b.size || ratio(b) - ratio(a);
        }
      : (a, b) => ratio(b) - ratio(a),
  );
  const used = new Array(scaffolds.length).fill(false);
  let bestUsed: ReachCon[] | null = null;
  let best = INF;
  let nodes = 0;
  const NODE_CAP = peakNodeCap;
  // `path` tracks the DFS pick order for collect; must reflect actual pick sequence so req-dependent
  // scaffolds follow the scaffolds that supply their requirements (not just sorted-array order).
  const path = opts?.collect ? ([] as ReachCon[]) : null;
  // `a` is the scaffold affinity built so far; `base` is affinity already held from the permanent build,
  // which legally bootstraps a scaffold (covers its requirement) without counting toward the deficit.
  function dfs(a: Vec, size: number): void {
    if (size >= best || nodes++ > NODE_CAP) return;
    const rem: Vec = [
      Math.max(0, need[0] - a[0]),
      Math.max(0, need[1] - a[1]),
      Math.max(0, need[2] - a[2]),
      Math.max(0, need[3] - a[3]),
      Math.max(0, need[4] - a[4]),
    ];
    if (rem[0] === 0 && rem[1] === 0 && rem[2] === 0 && rem[3] === 0 && rem[4] === 0) {
      best = size;
      if (path) bestUsed = [...path];
      return;
    }
    if (size + coverCostAt(table, rem) >= best) return; // cover table: cheapest extra subset, ignoring bootstrap
    const avail = addCap(base, a);
    for (let i = 0; i < scaffolds.length; i++) {
      const c = scaffolds[i]!;
      if (used[i] || size + c.size >= best || !covers(avail, c.req)) continue;
      used[i] = true;
      if (path) path.push(c);
      dfs(addCap(a, c.grant), size + c.size);
      if (path) path.pop();
      used[i] = false;
    }
  }
  dfs(zero(), 0);
  if (opts?.collect && bestUsed) {
    opts.collect.length = 0;
    opts.collect.push(...(bestUsed as ReachCon[]));
  }
  return best;
}

/** Split a self-covering build into its granting members, zero-grant size, and transient scaffold pool. */
function buildParts(cons: ReachCon[], B: ReachCon[]): { G: ReachCon[]; totalSize: number; pool: ReachCon[] } | null {
  let tot = zero();
  let mreq = zero();
  let totalSize = 0;
  for (const m of B) {
    tot = addCap(tot, m.grant);
    mreq = maxV(mreq, m.req);
    totalSize += m.size;
  }
  if (!covers(tot, mreq)) return null; // not self-covering
  const grants = (c: ReachCon) => c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4];
  const inB = new Set(B.map((b) => b.id));
  return { G: B.filter(grants), totalSize, pool: cons.filter((c) => !inB.has(c.id)) };
}

/**
 * The sampler's peel candidates, built back to front: each pick is the member whose requirement, with
 * every requirement not yet peeled, is covered by the OTHER unpeeled members' grants (plus the front), so
 * it needs no scaffold when it is placed last among them. Ties prefer the largest member (it defers size
 * to where no scaffold is held), then input order; when no member qualifies, the one with the smallest
 * summed deficit is peeled. This is the bootstrap heuristic's blind spot: it places the highest-requirement
 * member last, and a member whose requirement is only met with its own grant needs a scaffold whenever it
 * is placed, so placing it at the build's full size overshoots the peak.
 *
 * Two variants, one flag: with `zeroReqFirst` the zero-requirement members (crossroads) go in front,
 * which wins when their grants feed colors the build needs (every later deficit shrinks); without it
 * they are peeled like any other member, which wins when their colors are not needed at all, so placing
 * them first only inflates the size held at the step that still needs a scaffold. Each is one schedule,
 * RNG-free, mirrored in the Rust resolver (peel_order).
 */
function peelOrder(G: ReachCon[], zeroReqFirst: boolean): ReachCon[] {
  const reqFree = (c: ReachCon) =>
    zeroReqFirst && c.req[0] === 0 && c.req[1] === 0 && c.req[2] === 0 && c.req[3] === 0 && c.req[4] === 0;
  const front = G.filter(reqFree);
  const rest = G.filter((c) => !reqFree(c));
  let base = zero();
  for (const m of front) base = addCap(base, m.grant);
  const peeled: ReachCon[] = [];
  while (rest.length) {
    let mreq = zero();
    for (const m of rest) mreq = maxV(mreq, m.req);
    let pick = -1;
    let pickDef = Infinity;
    let pickSize = -1;
    for (let i = 0; i < rest.length; i++) {
      let others = base;
      for (let j = 0; j < rest.length; j++) if (j !== i) others = addCap(others, rest[j]!.grant);
      let def = 0;
      for (let k = 0; k < 5; k++) def += Math.max(0, mreq[k]! - others[k]!);
      const size = rest[i]!.size;
      if (def < pickDef || (def === pickDef && size > pickSize)) {
        pick = i;
        pickDef = def;
        pickSize = size;
      }
    }
    peeled.unshift(rest[pick]!);
    rest.splice(pick, 1);
  }
  return [...front, ...peeled];
}

/** What the sampler is searching for: a reachability witness (any order whose schedule fits the
 *  budget) or a quality schedule (the least-churn order among those that fit). */
type SamplerMode = "witness" | "quality";

/** The sampler's result: the member order it kept, that order's schedule peak, and its legal schedule
 *  when the peak fits the budget (the witness IS a schedule). Witness mode keeps the smallest-peak
 *  order, quality mode the least-churn one. */
interface SampledConstruction {
  peak: number;
  order: ReachCon[]; // the granting members in the order the mode kept
  tail: ReachCon[]; // the zero-grant members, placed last (they never raise the peak above the build size)
  steps: BuildStep[] | null;
  stepsFirst: BuildStep[] | null; // quality mode: steps-first argmin among fitting schedules
}

/** The guided climb's evaluation cap. Convergence measures under 32 evaluations on the real corpus,
 *  so 64 buys margin while bounding the worst case. A count, never wall-clock, for determinism. */
const CLIMB_EVALS = 64;

// Core sampler shared by minPeakSampled (which wants the peak), minPeakSampledOrder (which wants the
// witness order) and buildOrderPath (which wants the schedule). Every candidate order is scored by the
// peak of its actual legal schedule (emitSchedule: scaffolds added before the step that needs them,
// refunded the moment the rules allow, so a scaffold swap holds both sides until the old one may go).
// Both modes first score three deterministic orders - the bootstrap heuristic (lowest requirement
// first, then highest grant density) and both peel variants (peelOrder). Witness mode then samples up
// to `tries` seeded shuffles, keeping the smallest-peak order and stopping at the first schedule that
// fits the budget (a reachability proof needs nothing more). Quality mode instead hill-climbs each of
// the three heuristic starts independently (each capped at `climbEvals`, exploring its own basin):
// every start reads its own incumbent's non-crossroads scaffold buys and scores targeted reorderings,
// with every evaluation across all three climbs feeding one shared churn-then-steps argmin and the
// steps-first argmin alongside for the divergence harness. Shuffles remain only as a fallback for
// builds whose heuristic orders never fit: sample until one fits (capped by `tries`), then climb it.
function sampledConstruction(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget: number,
  tries: number,
  peakNodeCap: number,
  mode: SamplerMode = "witness",
  climbEvals: number = CLIMB_EVALS,
): SampledConstruction {
  const grants = (c: ReachCon) => c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4];
  const tail = B.filter((c) => !grants(c));
  const parts = buildParts(cons, B);
  if (!parts) return { peak: INF, order: [], tail, steps: null, stepsFirst: null };
  const { G, totalSize, pool } = parts;
  if (totalSize > budget) return { peak: INF, order: [], tail, steps: null, stepsFirst: null };
  const reqsum = (c: ReachCon) => c.req[0] + c.req[1] + c.req[2] + c.req[3] + c.req[4];
  const ratio = (c: ReachCon) => (c.grant[0] + c.grant[1] + c.grant[2] + c.grant[3] + c.grant[4]) / c.size;
  const order = [...G].sort((a, b) => reqsum(a) - reqsum(b) || ratio(b) - ratio(a));
  let best = INF;
  let bestOrder: ReachCon[] = [];
  let bestSteps: BuildStep[] | null = null;
  let bestChurn = Infinity; // quality: churn-then-steps argmin among fitting schedules
  let stepsFirst: BuildStep[] | null = null;
  let sfChurn = Infinity;
  // Global-argmin bookkeeping for quality mode, shared by consider and climbFromStart so every
  // evaluated candidate - whichever start's climb produced it - competes for the same incumbent:
  // churn-then-steps argmin among fitting schedules, plus the steps-first argmin for the divergence
  // harness.
  const record = (candidate: ReachCon[], sched: Schedule | null): void => {
    const peak = sched ? sched.peak : INF;
    if (sched?.steps) {
      const c = churnPoints(sched.steps);
      const n = sched.steps.length;
      if (bestSteps === null || c < bestChurn || (c === bestChurn && n < bestSteps.length)) {
        best = peak;
        bestOrder = [...candidate];
        bestSteps = sched.steps;
        bestChurn = c;
      }
      if (stepsFirst === null || n < stepsFirst.length || (n === stepsFirst.length && c < sfChurn)) {
        stepsFirst = sched.steps;
        sfChurn = c;
      }
    } else if (bestSteps === null && peak < best) {
      best = peak; // nothing fits yet: keep chasing the lowest peak, as the witness does
      bestOrder = [...candidate];
    }
  };
  // Score a candidate; witness mode returns true when it fits (the caller stops sampling),
  // quality mode always returns false so the heuristic-start scoring loop never early-exits (tries only caps the fallback shuffles).
  const consider = (candidate: ReachCon[]): boolean => {
    const sched = emitSchedule(candidate, tail, pool, table, budget, peakNodeCap);
    const peak = sched ? sched.peak : INF;
    if (mode === "witness") {
      if (peak < best) {
        best = peak;
        bestOrder = [...candidate];
        bestSteps = sched?.steps ?? null;
      }
      return best <= budget;
    }
    record(candidate, sched);
    return false;
  };
  // Candidate reorderings around a schedule's first two non-crossroads scaffold buys: advance a
  // later feeder ahead of the buy, push the triggering member to the end, or swap it one step earlier.
  const movesFor = (byId: Map<string, ReachCon>, ord: ReachCon[], steps: BuildStep[]): ReachCon[][] => {
    const out: ReachCon[][] = [];
    const idx = new Map(ord.map((c, i) => [c.id, i]));
    let events = 0;
    for (let s = 0; s < steps.length && events < 2; s++) {
      const st = steps[s]!;
      if (st.kind !== "scaffold-add" || st.conId.startsWith("crossroads_")) continue;
      events++;
      const scaffold = byId.get(st.conId);
      // The triggering member: the first completion after the buy that is in the granting order.
      let ti = -1;
      for (let t = s + 1; t < steps.length; t++) {
        const c = steps[t]!;
        if (c.kind === "complete" && idx.has(c.conId)) {
          ti = idx.get(c.conId)!;
          break;
        }
      }
      if (ti < 0) continue;
      if (scaffold) {
        // Advance each later member that feeds the scaffold's colors to just before the trigger.
        for (let j = ti + 1; j < ord.length; j++) {
          let feeds = false;
          for (let k = 0; k < 5; k++) if (scaffold.grant[k]! > 0 && ord[j]!.grant[k]! > 0) feeds = true;
          if (!feeds) continue;
          const cand = [...ord];
          const [m] = cand.splice(j, 1);
          cand.splice(ti, 0, m!);
          out.push(cand);
        }
      }
      if (ti + 1 < ord.length) {
        const cand = [...ord];
        const [m] = cand.splice(ti, 1);
        cand.push(m!);
        out.push(cand);
      }
      if (ti > 0) {
        const cand = [...ord];
        const tmp = cand[ti - 1]!;
        cand[ti - 1] = cand[ti]!;
        cand[ti] = tmp;
        out.push(cand);
      }
    }
    return out;
  };
  // Guided local search from one heuristic start: tracks a LOCAL incumbent so this start explores its
  // own basin (churn-then-steps improvements, accepted greedily until convergence or `cap`), while
  // every evaluation still feeds the global argmin through record.
  const climbFromStart = (start: ReachCon[], cap: number): void => {
    const first = emitSchedule(start, tail, pool, table, budget, peakNodeCap);
    if (!first?.steps) return;
    let curOrder = start;
    let curSteps = first.steps;
    let curChurn = churnPoints(curSteps);
    const byId = new Map(pool.map((c) => [c.id, c]));
    let evals = 0;
    for (let improved = true; improved && evals < cap; ) {
      improved = false;
      for (const cand of movesFor(byId, curOrder, curSteps)) {
        if (evals >= cap) break;
        evals++;
        const sched = emitSchedule(cand, tail, pool, table, budget, peakNodeCap);
        record(cand, sched);
        if (sched?.steps) {
          const c = churnPoints(sched.steps);
          if (c < curChurn || (c === curChurn && sched.steps.length < curSteps.length)) {
            curOrder = cand;
            curSteps = sched.steps;
            curChurn = c;
            improved = true;
            break;
          }
        }
      }
    }
  };
  const done = (): SampledConstruction => ({ peak: best, order: bestOrder, tail, steps: bestSteps, stepsFirst });
  if (consider(order)) return done();
  for (const zeroReqFirst of [true, false]) if (consider(peelOrder(G, zeroReqFirst))) return done();
  let seed = (totalSize * 2654435761 + G.length * 40503) >>> 0; // deterministic per build
  const rnd = () => {
    seed = (seed + 0x6d2b79f5) >>> 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const shuffle = (): void => {
    for (let i = order.length - 1; i > 0; i--) {
      const j = Math.floor(rnd() * (i + 1));
      const tmp = order[i]!;
      order[i] = order[j]!;
      order[j] = tmp;
    }
  };
  if (mode === "quality") {
    for (const start of [order.slice(), peelOrder(G, true), peelOrder(G, false)]) climbFromStart(start, climbEvals);
    // Fallback for builds whose heuristic orders never fit: sample until one does, then climb it.
    if (bestSteps === null) {
      for (let attempt = 0; attempt < tries && bestSteps === null; attempt++) {
        shuffle();
        consider(order);
      }
      climbFromStart(order.slice(), climbEvals);
    }
    return done();
  }
  for (let attempt = 0; attempt < tries && best > budget; attempt++) {
    shuffle();
    consider(order);
  }
  return done();
}

/**
 * Fast sound construction-peak witness for the self-covering whole-build `B`: the smallest schedule peak
 * among the sampled orders (see sampledConstruction). A peak at or under budget is a GENUINE witness: that
 * order's schedule builds `B` within budget and replays legally through the oracle (web/test/
 * witness-schedule.test.ts pins this), so it is SOUND for "reachable" - it can only flip a false-dim, never
 * invent a false-reach. It does not compute the true minimum, so it may overshoot a hard-to-sample
 * reachable build (a conservative dim, closed only by the exact engine). Deterministic. INF if `B` is not
 * self-covering.
 */
export function minPeakSampled(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget = BUDGET,
  tries = 8,
  peakNodeCap = 3000,
): number {
  return sampledConstruction(cons, table, B, budget, tries, peakNodeCap).peak;
}

/**
 * The construction ORDER behind the witness: the constellations of self-covering build `B` in an order
 * whose schedule builds it within `budget` points held at once (granting members first, in their
 * peak-minimizing order, then the zero-grant members), or null when no sampled order fits the budget.
 * buildOrderPath turns it into the step-by-step schedule (scaffolds held and refunded). Deterministic.
 */
export function minPeakSampledOrder(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget = BUDGET,
  tries = 16,
  peakNodeCap = 3000,
): ReachCon[] | null {
  const { peak, order, tail } = sampledConstruction(cons, table, B, budget, tries, peakNodeCap);
  return peak <= budget ? [...order, ...tail] : null;
}

/**
 * Need-driven greedy member ordering: the build builds itself. Forward-constructs an order of B's
 * granting members so each is activated by the accumulated grants of the members already placed,
 * plus the ever-present crossroads seed (one point per color is always reachable through a
 * refundable crossroads; the emission replay decides whether one is actually bought). Among
 * candidates it picks the densest contributor to the still-deficient colors (points granted in
 * deficient colors per star - the Scholar's Light tiebreak), ratios compared exactly by
 * cross-multiplication, ties broken by id. When nothing activates, the build is genuinely stuck
 * without scaffolding: it places the member with the smallest summed deficit and lets the emission
 * replay buy exactly that gap. Zero-grant members go to `tail`, placed last. Canonicalized and
 * deterministic like buildOrderPath; null only when B is not self-covering (one honest signal).
 */
export function needDrivenOrder(cons: ReachCon[], B: ReachCon[]): { order: ReachCon[]; tail: ReachCon[] } | null {
  B = [...B].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)); // canonical: a function of the SET
  const parts = buildParts(cons, B);
  if (!parts) return null; // not self-covering
  const { G } = parts;
  const grants = (c: ReachCon) => c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4];
  const tail = B.filter((c) => !grants(c));
  let seed = zero();
  for (const c of cons) if (c.id.startsWith("crossroads_")) seed = addCap(seed, c.grant);
  const placed = new Array(G.length).fill(false);
  const order: ReachCon[] = [];
  let grant = zero();
  while (order.length < G.length) {
    const avail = addCap(grant, seed);
    // Deficient colors: what unplaced members still demand beyond the accumulated grants.
    const deficit = zero();
    for (let i = 0; i < G.length; i++) {
      if (placed[i]) continue;
      for (let k = 0; k < 5; k++) {
        const short = G[i]!.req[k]! - grant[k]!;
        if (short > deficit[k]!) deficit[k] = short;
      }
    }
    let pick = -1;
    let pickPts = 0;
    let pickSize = 1;
    for (let i = 0; i < G.length; i++) {
      if (placed[i] || !covers(avail, G[i]!.req)) continue;
      let pts = 0;
      for (let k = 0; k < 5; k++) if (deficit[k]! > 0) pts += G[i]!.grant[k]!;
      if (
        pick < 0 ||
        pts * pickSize > pickPts * G[i]!.size ||
        (pts * pickSize === pickPts * G[i]!.size && G[i]!.id < G[pick]!.id)
      ) {
        pick = i;
        pickPts = pts;
        pickSize = G[i]!.size;
      }
    }
    if (pick < 0) {
      // Genuinely stuck: place the member with the smallest summed deficit; the emission replay's
      // peakToReach buys exactly that gap (crossroads-biased, minimal) and refunds it legally.
      let pickDef = Infinity;
      for (let i = 0; i < G.length; i++) {
        if (placed[i]) continue;
        let d = 0;
        for (let k = 0; k < 5; k++) d += Math.max(0, G[i]!.req[k]! - grant[k]!);
        if (d < pickDef || (d === pickDef && G[i]!.id < G[pick]!.id)) {
          pick = i;
          pickDef = d;
        }
      }
    }
    placed[pick] = true;
    order.push(G[pick]!);
    grant = addCap(grant, G[pick]!.grant);
  }
  return { order, tail };
}

/** One step of a guided build order. `points` is the constellation's star count (negative on refund);
 *  `heldAfter` is the running points held after the step, which never exceeds the cap. */
export type BuildStep =
  | { kind: "complete"; conId: string; points: number; heldAfter: number }
  | { kind: "scaffold-add"; conId: string; points: number; heldAfter: number }
  | { kind: "scaffold-refund"; conId: string; points: number; heldAfter: number };

/** Points spent on non-crossroads scaffolds in a schedule: the churn the ordering minimizes
 *  (crossroads are free by definition - the objective is zero when a build bootstraps from
 *  crossroads alone). Exported as the shared quality metric for tests and harnesses. */
export function churnPoints(steps: BuildStep[]): number {
  let pts = 0;
  for (const s of steps) if (s.kind === "scaffold-add" && !s.conId.startsWith("crossroads_")) pts += s.points;
  return pts;
}

/** A member order's legal schedule: the most points held at once, and the steps when that fits the budget. */
interface Schedule {
  peak: number; // when over budget: the running total at the first over-cap add, itself already over
  steps: BuildStep[] | null; // present exactly when peak <= budget
}

/** The scaffold-search node cap for the cold path (the panel's schedule): find the exact min subset,
 *  immune to the witness's sampling cap. */
const REPLAY_CAP = 300_000;

/**
 * Emit the add/complete/refund schedule for a member `order` plus zero-grant `tail`: at each step,
 * hold the exact scaffold SET peakToReach picks (crossroads-biased), draining refunds the moment
 * they are legal (docs/devotion-system.md: removal cannot strand a dependent). A scaffold swap adds
 * the new scaffold before the old one may go, so the peak counts both, as the game does. Returns the
 * peak, and the steps only when it fits `budget`; null when a step's deficit cannot be scaffolded or
 * a held scaffold can never be legally refunded - the honest signal that this ORDER does not work;
 * the caller decides what other order to try. This is the one legality-bearing loop: the peak witness
 * scores every candidate order with it, and buildOrderPath renders its steps.
 */
function emitSchedule(
  order: ReachCon[],
  tail: ReachCon[],
  pool: ReachCon[],
  table: CoverTable,
  budget: number,
  peakNodeCap = REPLAY_CAP,
): Schedule | null {
  const steps: BuildStep[] = [];
  let peak = 0;
  const step = (kind: BuildStep["kind"], conId: string, points: number, heldAfter: number): void => {
    steps.push({ kind, conId, points, heldAfter });
    if (heldAfter > peak) peak = heldAfter;
  };
  let grant: Vec = zero();
  let mreq: Vec = zero(); // max requirement incl. the member being placed (drives the scaffold need-set)
  let creq: Vec = zero(); // max requirement over COMPLETED members only (drives refund legality)
  let held: ReachCon[] = [];
  let running = 0;
  const over = (): Schedule => ({ peak: running, steps: null });
  // In-game refund rule: a scaffold may be refunded only if everything still standing (completed
  // members plus the other held scaffolds) keeps its requirement covered without the scaffold's grant.
  const canRefund = (s: ReachCon, keep: ReachCon[]): boolean => {
    let g = grant;
    let r = maxV(creq, s.req);
    for (const k of keep) {
      g = addCap(g, k.grant);
      r = maxV(r, k.req);
    }
    return covers(g, r);
  };
  // Refund every held scaffold outside `needIds` that is safe to drop; unsafe ones stay held and are
  // retried after later adds supply replacement grants. Fixed point: one refund can unlock another.
  const drainRefunds = (needIds: Set<string>): void => {
    for (let progress = true; progress; ) {
      progress = false;
      for (const s of [...held]) {
        if (needIds.has(s.id)) continue;
        const keep = held.filter((k) => k.id !== s.id);
        if (!canRefund(s, keep)) continue;
        held = keep;
        running -= s.size;
        step("scaffold-refund", s.id, -s.size, running);
        progress = true;
      }
    }
  };
  for (const m of order) {
    mreq = maxV(mreq, m.req);
    const def: Vec = [
      Math.max(0, mreq[0] - grant[0]),
      Math.max(0, mreq[1] - grant[1]),
      Math.max(0, mreq[2] - grant[2]),
      Math.max(0, mreq[3] - grant[3]),
      Math.max(0, mreq[4] - grant[4]),
    ];
    const need: ReachCon[] = [];
    const sz = peakToReach(pool, table, def, grant, peakNodeCap, { collect: need, preferSmall: true });
    if (sz >= INF) return null;
    const needIds = new Set(need.map((s) => s.id));
    drainRefunds(needIds);
    const heldIds = new Set(held.map((s) => s.id));
    for (const s of need)
      if (!heldIds.has(s.id)) {
        held.push(s);
        running += s.size;
        if (running > budget) return over(); // an over-cap add is illegal: no steps, never an illegal step
        step("scaffold-add", s.id, s.size, running);
      }
    drainRefunds(needIds); // retry refunds the new scaffolds' grants may have made safe
    running += m.size;
    step("complete", m.id, m.size, running);
    if (running > budget) return over();
    grant = addCap(grant, m.grant);
    creq = maxV(creq, m.req);
  }
  drainRefunds(new Set());
  if (held.length) return null; // a scaffold no drain can legally refund: honest null, never an illegal step
  for (const t of tail) {
    running += t.size;
    step("complete", t.id, t.size, running);
    if (running > budget) return over();
  }
  return { peak, steps };
}

/** Both generators' schedules for `B`, before the churn-then-steps pick: the need-driven greedy's
 *  and the sampler's (re-emitted at the cold-path cap, its sampled schedule as fallback).
 *  `samplerStepsFirst` is the steps-first argmin among the fitting sampled schedules, at the
 *  sampling cap, kept for the divergence harness; only the churn-first pick is re-emitted at the
 *  cold-path cap. The pick itself stays churn-first and lives in buildOrderPath.
 *  climbEvals is harness-facing (the climb-off/on comparison); app callers take the default. */
export interface OrderCandidates {
  greedy: BuildStep[] | null;
  sampler: BuildStep[] | null;
  samplerStepsFirst: BuildStep[] | null;
}

export function buildOrderCandidates(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget = BUDGET,
  tries = 16,
  peakNodeCap = 3000,
  climbEvals = CLIMB_EVALS,
): OrderCandidates {
  B = [...B].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const parts = buildParts(cons, B);
  if (!parts || parts.totalSize > budget) return { greedy: null, sampler: null, samplerStepsFirst: null };
  const nd = needDrivenOrder(cons, B);
  const viaGreedy = nd ? (emitSchedule(nd.order, nd.tail, parts.pool, table, budget)?.steps ?? null) : null;
  const sc = sampledConstruction(cons, table, B, budget, tries, peakNodeCap, "quality", climbEvals);
  const viaSampler =
    sc.steps === null ? null : (emitSchedule(sc.order, sc.tail, parts.pool, table, budget)?.steps ?? sc.steps);
  return { greedy: viaGreedy, sampler: viaSampler, samplerStepsFirst: sc.stepsFirst };
}

/**
 * A legal constellation-level order that assembles the self-covering build `B` within `budget` points
 * held at once, including the transient scaffold to ADD before a step and REFUND once the build's own
 * grants cover it. Two candidate orders are emitted (emitSchedule holds the exact scaffold SET
 * peakToReach picks and drains refunds per the in-game rules, docs/devotion-system.md): the
 * need-driven greedy order (needDrivenOrder: the build builds itself, usually from crossroads alone)
 * and the guided quality search (sampledConstruction: heuristic starts plus a schedule-guided climb).
 * Neither generator dominates - the greedy wins cap-tight builds the sampler scaffolds heavily, the
 * sampler's bootstrap heuristic wins typical builds the greedy misorders - so the better schedule by
 * the ordering objective is returned: fewer churn points (churnPoints), then fewer steps, the greedy
 * on a full tie. Per-build best-of-both is never worse than either generator alone. Returns null when
 * neither order fits the budget or a held scaffold can never be legally refunded - the honest "not
 * validly buildable" signal. No order is better than an illegal order. Input is canonicalized (sorted
 * by constellation id), so the output is a pure function of the build set - every caller gets the
 * identical order.
 */
export function buildOrderPath(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget = BUDGET,
  tries = 32,
  peakNodeCap = 3000,
): BuildStep[] | null {
  const { greedy, sampler } = buildOrderCandidates(cons, table, B, budget, tries, peakNodeCap);
  if (!greedy || !sampler) return greedy ?? sampler;
  const g = churnPoints(greedy);
  const s = churnPoints(sampler);
  if (g !== s) return g < s ? greedy : sampler;
  return greedy.length <= sampler.length ? greedy : sampler;
}

/** The same schedule at a large fallback budget: it recovers cliff builds whose heuristic orders
 *  never fit and the live fallback cap misses. Quality comes from the guided climb, which both paths
 *  share, so on typical builds this matches the live result; no app control calls it, and it never
 *  belongs on the live/per-click path. */
export function buildOrderEscalated(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  budget = BUDGET,
): BuildStep[] | null {
  return buildOrderPath(cons, table, B, budget, 4096, 3000);
}

/**
 * The fewest points at which this selection has a sampled legal build order (>= fromCap, <= budget),
 * or null if none up to budget. A build's construction can need a transient scaffold whose point cost
 * pushes the running total above the selection's own size, so a selection valid at N points may only be
 * assemblable at N+k. This reports that k on demand, to explain a missing order ("no path in fewer than
 * M points") rather than leaving a bare null. Sampled, so the cap returned is an upper bound on the true
 * minimum; heavy (it re-searches per cap), so never run it on the live per-click path.
 */
export function minBuildableCap(
  cons: ReachCon[],
  table: CoverTable,
  B: ReachCon[],
  fromCap: number,
  budget = BUDGET,
  tries = 256,
): number | null {
  for (let cap = Math.max(fromCap, 0); cap <= budget; cap++) {
    if (buildOrderPath(cons, table, B, cap, tries)) return cap;
  }
  return null;
}

/**
 * Exact reachability decision (the "unknown" gap-closer). DFS over filler subsets, pruned by the
 * cover table as an admissible lower bound, early-exiting on the first valid build within budget.
 * For a reachable claim it stops at the first witness; for a dim claim it exhausts (the cover
 * prune keeps that bounded). Definitive: returns the true reachable/dim answer.
 *
 * Run it only on candidates the cheap bracket left "unknown" - it is heavier than the bracket
 * (worst seen ~700k nodes for one borderline candidate), so it is not for an unfiltered sweep.
 */
export function reachableExact(cons: ReachCon[], table: CoverTable, claimedIds: string[], budget = BUDGET): boolean {
  const byId = new Map(cons.map((c) => [c.id, c]));
  const claimed = claimedIds.map((id) => byId.get(id)!);
  return reachableExactFrom(cons, table, stateFromClaimed(claimed), budget);
}

/**
 * Exact reachability decision for a selection state. Definitive (the true reachable/dim answer).
 *
 * Partial finishes break the bare cover prune: the table is built from whole-constellation grants,
 * so it cannot see that finishing a started constellation supplies affinity more cheaply, and the
 * prune over-estimates the remaining cost (falsely dimming). Crediting the finishes keeps it sound
 * but so loose the search explodes. So we DECIDE the finishes up front: enumerate which to complete
 * (very few - one per started granting constellation), and for each choice run a pure
 * whole-constellation DFS whose cover prune is then both admissible AND tight (no cheaper finishes
 * remain to undercut it). Reachable iff some finish-choice yields a covering, constructible build
 * within budget. With no partial finishes this is exactly the single whole-constellation DFS.
 */
let exactNodes = 0;
/** Nodes visited by the last reachableExactFrom call (diagnostic / cap tuning). */
export function lastExactNodes(): number {
  return exactNodes;
}

export function reachableExactFrom(cons: ReachCon[], table: CoverTable, st: ReachState, budget = BUDGET): boolean {
  exactNodes = 0;
  const ratio = (c: ReachCon) => (c.grant[0] + c.grant[1] + c.grant[2] + c.grant[3] + c.grant[4]) / c.size;
  const wholeFiller = cons
    .filter((c) => !st.startedIds.has(c.id) && (c.grant[0] || c.grant[1] || c.grant[2] || c.grant[3] || c.grant[4]))
    .sort((a, b) => ratio(b) - ratio(a));
  const pf = st.partialFinish;
  const grantById = new Map(pf.map((p) => [p.id, p.grant]));
  const remainingById = new Map(pf.map((p) => [p.id, p.remaining]));
  let found = false;
  const chosen: ReachCon[] = [];
  // Whole-constellation DFS for one finish-choice; the cover prune is admissible (no finishes left).
  function rec(i: number, build: Vec, cost: number, maxReqPlaced: Vec, builtCons: ReachCon[]): void {
    if (found) return;
    exactNodes++;
    if (covers(build, maxReqPlaced)) {
      // A covering node is self-covering (build covers maxReqPlaced), so every remaining filler is optional
      // and refundable. The peak witness already models using such affinity as a transient refundable
      // scaffold, which is never worse for the peak than keeping it permanent - so the gate-then-witness
      // verdict HERE is FINAL: adding more filler cannot lower the construction peak. Decide and RETURN,
      // pruning the entire post-covering superset subtree (the dominant search cost). Because covering nodes
      // are now bounded, every one is witnessed (no call cap), which makes the verdict order-independent so
      // the Rust/WASM port stays verdict-equivalent. The cheap ladder gate is a sound sufficient proof; the
      // witness models the scaffold-then-refund peak. constructible alone is NOT sufficient - it ignores the
      // transient crossroads peak and was the source of the off-by-one false-reaches.
      const members = [...builtCons, ...chosen];
      if (
        peakGateReachable(cons, members, budget) ||
        minPeakSampled(cons, table, members, budget, GATE_WITNESS_TRIES, PEAK_NODE_CAP) <= budget
      )
        found = true;
      return;
    }
    if (i >= wholeFiller.length) return;
    const target = maxV(maxReqPlaced, st.target);
    const deficit: Vec = [
      Math.max(0, target[0] - build[0]),
      Math.max(0, target[1] - build[1]),
      Math.max(0, target[2] - build[2]),
      Math.max(0, target[3] - build[3]),
      Math.max(0, target[4] - build[4]),
    ];
    if (cost + coverCostAt(table, deficit) > budget) return; // even the cheapest completion overflows
    const c = wholeFiller[i]!;
    if (cost + c.size <= budget) {
      chosen.push(c);
      rec(i + 1, addCap(build, c.grant), cost + c.size, maxV(maxReqPlaced, c.req), builtCons);
      chosen.pop();
    }
    if (!found) rec(i + 1, build, cost, maxReqPlaced, builtCons);
  }
  // Decide every subset of partial finishes to complete (2^k with k tiny - usually 0 or 1).
  for (let mask = 0; mask < 1 << pf.length && !found; mask++) {
    let build0: Vec = [...st.supply],
      cost0 = st.own;
    const finished = new Set<string>();
    for (let j = 0; j < pf.length; j++)
      if (mask & (1 << j)) {
        build0 = addCap(build0, pf[j]!.grant);
        cost0 += pf[j]!.remaining;
        finished.add(pf[j]!.id);
      }
    if (cost0 > budget) continue;
    // A finished partial carries its full grant AND its full size (selected + remaining): the witness peak
    // math reads member.size, so a finished partial must count its whole constellation, not just the stars
    // already selected, or the peak is undercounted and an over-budget build wrongly looks reachable.
    const builtCons = st.built.map((b) =>
      finished.has(b.id) ? { ...b, grant: grantById.get(b.id)!, size: b.size + remainingById.get(b.id)! } : b,
    );
    chosen.length = 0;
    rec(0, build0, cost0, st.target, builtCons);
  }
  return found;
}

/** Signature of the exact gap-resolver; the WASM port is a drop-in for the TS `reachableExactFrom`. */
export type ExactResolver = (cons: ReachCon[], table: CoverTable, st: ReachState, budget: number) => boolean;
// The bracket gap is resolved by this. Defaults to the TS search; an adapter may swap in the
// (verdict-equivalent, far faster) WASM port via setExactResolver. Pure default keeps the core testable.
let exactResolver: ExactResolver = reachableExactFrom;
/** Override the exact gap-resolver (pass null to restore the TS default). */
export function setExactResolver(fn: ExactResolver | null): void {
  exactResolver = fn ?? reachableExactFrom;
}

/**
 * Classify a partial-selection state by bracketing minCost, then resolving the gap exactly.
 * `lowerBoundFrom` soundly proves "dim"; `peakGateReachable` then the peak witness soundly prove
 * "reachable"; the rare remainder is decided by the exact resolver (TS or the injected WASM port).
 * Always returns "reachable" or "dim", deciding reachability on the construction peak (not the
 * post-refund cost), so a build whose peak overflows the budget is never lit.
 */
export function classifyForSelection(cons: ReachCon[], table: CoverTable, st: ReachState, budget = BUDGET): Reach {
  if (lowerBoundFrom(table, st) > budget) return "dim";
  // Cheap SOUND reachable proof, charging the transient crossroads peak greedy's refunded cost ignores.
  // greedy's seed-unlock order is realizable while holding one crossroads per color it had to bootstrap,
  // so its peak is greedyCost + lastGreedyBootColors() - a sound, tight upper bound (Ted's ladder: each
  // color is a one-time bottom-of-ladder cost, often far below 5). This both restores the old greedy's
  // O(1) reach for near-ceiling builds AND dims the false-reaches: a tier-1 constellation at budget 3 has
  // greedyCost 3 + 1 bootstrapped color = 4 > 3, so it defers to the witness, which dims it. The narrow
  // band where the peak still overflows falls through to the witness and exact resolver, which model it.
  const g = greedyFrom(cons, st, budget);
  if (g + lastGreedyBootColors() <= budget) return "reachable";
  // Peak-bounded witness for the exact resolver's blind spot. Its constructibility check models only
  // the transient crossroads seed, so it wrongly dims tight self-covering builds that are reachable only by
  // holding transient refundable scaffolding (a crossroads or constellation beyond the seed) until the
  // build self-covers, then refunding it. When the started set is itself a complete whole-constellation
  // build (no partials), minPeakSampled samples real construction orders: a sampled peak <= budget is a
  // genuine build order, so this only ever flips a false-dim to reachable and never introduces a
  // false-reach. It early-exits on the first witness (fast for reachable builds) and tries a bounded
  // number of orders otherwise, so even a sweep of dozens of near-budget candidates stays in the ms.
  //
  // Run BEFORE the exact resolver: the witness is ~0.1ms and the resolver is the expensive (WASM/DFS)
  // call, so a witness hit short-circuits it. Verdict-identical - the witness only returns reachable on a
  // real order the definitive resolver would also accept - it just saves the resolver on self-covering
  // reachable candidates. Applies only to a complete started build (no partials); minPeakSampled itself
  // returns instantly for a non-self-covering build, so no budget-proximity gate is needed (an own>=49
  // gate was measured to wrongly exclude ~6% of the false-dim region, self-covering builds at own 44-48).
  if (st.partialFinish.length === 0) {
    if (minPeakSampled(cons, table, st.built, budget, PEAK_WITNESS_TRIES, PEAK_NODE_CAP) <= budget) return "reachable";
  }
  if (exactResolver(cons, table, st, budget)) return "reachable";
  return "dim";
}

/** Like classify, but resolves the "unknown" gap with the exact resolver - always reachable or dim. */
export function classifyComplete(
  cons: ReachCon[],
  table: CoverTable,
  claimedIds: string[],
  budget = BUDGET,
): "reachable" | "dim" {
  const verdict = classify(cons, table, claimedIds, budget);
  if (verdict !== "unknown") return verdict;
  return reachableExact(cons, table, claimedIds, budget) ? "reachable" : "dim";
}

/** Minimum total stars to COMPLETE conId on top of `selected`, or INF if not within maxBudget. */
export function completionMinCost(
  model: DevotionModel,
  cons: ReachCon[],
  table: CoverTable,
  selected: Set<string>,
  conId: string,
  maxBudget = BUDGET,
): number {
  const con = model.constellations.get(conId);
  if (!con) return INF;
  const withCon = new Set(selected);
  for (const sid of con.starIds) withCon.add(sid);
  const st = selectionSummary(model, withCon);
  let lo = st.own; // cannot cost less than the stars already required
  if (lo > maxBudget) return INF;
  if (exactResolver(cons, table, st, maxBudget) === false) return INF;
  let hi = maxBudget;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (exactResolver(cons, table, st, mid)) hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

/**
 * Minimum total points for `selected` to be a valid, constructible build - the smallest budget at
 * which the selection classifies "reachable". This is the slider floor: below it the current
 * selection cannot be a legal build (e.g. claiming Leviathan alone is 7 stars but needs ~26 points
 * once the affinity it requires is paid for). Returns `own` when nothing gates the selection, 0 when
 * empty, and never exceeds maxBudget (a selection needing more pins to maxBudget). Monotone in
 * budget, so a binary search over classifyForSelection is exact.
 */
export function selectionMinCost(
  model: DevotionModel,
  cons: ReachCon[],
  table: CoverTable,
  selected: Set<string>,
  maxBudget = BUDGET,
): number {
  const st = selectionSummary(model, selected);
  if (st.own === 0) return 0;
  if (st.own >= maxBudget) return maxBudget;
  return minCostFrom(cons, table, st, maxBudget) ?? maxBudget;
}

/**
 * The smallest budget in [own, maxBudget] at which `st` classifies "reachable", or null when even
 * maxBudget does not (a binary search: the verdict is monotone in budget). Callers that only need
 * a number within the cap wrap it (selectionMinCost); callers explaining a dim verdict pass a bound
 * past the cap so they can say how many points the target really needs.
 */
export function minCostFrom(cons: ReachCon[], table: CoverTable, st: ReachState, maxBudget: number): number | null {
  let lo = st.own; // cannot cost less than the stars already selected
  if (lo > maxBudget) return null;
  if (classifyForSelection(cons, table, st, maxBudget) !== "reachable") return null;
  let hi = maxBudget;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (classifyForSelection(cons, table, st, mid) === "reachable") hi = mid;
    else lo = mid + 1;
  }
  return lo;
}

export interface ReachView {
  completable: Set<string>;
  // Every unselected star whose path (the star plus its unselected predecessors) keeps the selection
  // reachable at the sweep budget: all unselected stars of a completable constellation, plus the stars
  // within reach of a partially enterable one (path cost <= maxK - see reachabilityForSelection).
  reachableStars: Set<StarId>;
  have: Vec; // true in-game affinity total from completed constellations (uncapped; the panel's have column)
  need: Vec;
  needSource: Map<number, string[]>;
  /** The selection is a legal build on its own: valid (every started constellation's requirement is
   *  met by the completed ones' affinity, docs/devotion-system.md) and constructible (it classifies
   *  reachable at the sweep budget, which is never below the validity floor nor above 55, so that
   *  verdict is the verdict at 55). "Reachable" alone is weaker: it means the selection can be held
   *  within the budget with scaffolding standing, which the game would not let you finish on. */
  legal: boolean;
}

/** One full sweep for a selection: what can be completed, what stars are reachable, and the panel vectors. */
export function reachabilityForSelection(
  model: DevotionModel,
  cons: ReachCon[],
  table: CoverTable,
  selected: Set<StarId>,
  budget = BUDGET,
): ReachView {
  const st = selectionSummary(model, selected);
  const completable = new Set<string>();
  const reachableStars = new Set<StarId>();
  // A constellation already fully selected "completes" to the current selection unchanged, so its
  // verdict is the current selection's - classify that once and reuse it instead of re-running the
  // (sometimes costly) resolver per complete constellation.
  const selfReachable = classifyForSelection(cons, table, st, budget) === "reachable";
  // The validity definition in docs/devotion-system.md ("A selection is valid when..."), checked on
  // the summary: no started constellation may need more of a color than the completed ones supply.
  // Same comparison the affinity panel shows as have/need.
  const selfValid = st.target.every((need, i) => need <= st.supplyUncapped[i]!);
  for (const c of model.constellations.values()) {
    const size = c.starIds.length;
    let selCount = 0;
    for (const sid of c.starIds) if (selected.has(sid)) selCount++;
    if (selCount === size) {
      if (selfReachable) completable.add(c.id);
      continue;
    }
    // The verdict for "selection + k stars of c" depends only on the count k (selectionSummary reduces
    // a selection to per-constellation counts), so the probe set is the selection plus unselected stars
    // of c in index order until the count reaches k - WHICH stars is immaterial to the verdict.
    const reachableAt = (k: number): boolean => {
      const withK = new Set(selected);
      let count = selCount;
      for (const sid of c.starIds) {
        if (count >= k) break;
        if (!withK.has(sid)) {
          withK.add(sid);
          count++;
        }
      }
      return classifyForSelection(cons, table, selectionSummary(model, withK), budget) === "reachable";
    };
    if (reachableAt(size)) {
      completable.add(c.id);
      for (const sid of c.starIds) if (!selected.has(sid)) reachableStars.add(sid);
      continue;
    }
    // Not completable: find maxK, the largest star count that stays reachable. Probe the cheapest
    // entry (selCount+1) first - most non-completable constellations admit nothing, and that probe is
    // the same dim proof the old per-frontier-star pass paid - then binary search the rest (the
    // verdict is monotone in k: a bigger proper prefix costs more and grants nothing until complete).
    let lo = selCount + 1;
    const last = size - 1; // k = size is the completable question, already answered above
    if (lo > last || !reachableAt(lo)) continue;
    let hi = last;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if (reachableAt(mid)) lo = mid;
      else hi = mid - 1;
    }
    const maxK = lo;
    for (const sid of c.starIds) {
      if (selected.has(sid)) continue;
      if (selCount + pathToStar(model, selected, sid).size <= maxK) reachableStars.add(sid);
    }
  }
  // panel: have = supplyUncapped (the true total, not the engine's CAP_MAX-clamped supply),
  // need = target, needSource = started cons defining each color's max.
  const needSource = new Map<number, string[]>();
  for (let i = 0; i < 5; i++) {
    if (st.target[i] === 0) continue;
    const src: string[] = [];
    for (const conId of st.startedIds) {
      const c = model.constellations.get(conId)!;
      if ((vecOf(c.affinityRequired)[i] ?? 0) === st.target[i]) src.push(conId);
    }
    needSource.set(i, src);
  }
  return {
    completable,
    reachableStars,
    have: st.supplyUncapped,
    need: st.target,
    needSource,
    legal: selfReachable && selfValid,
  };
}

/** The full engine result one UI refresh needs for a selection: the validity floor and the sweep. */
export interface SelectionView {
  minCost: number; // selectionMinCost: fewest points that keep this selection a legal build (the slider floor)
  reach: ReachView; // reachabilityForSelection: dimming, reachable stars, and the affinity panel vectors
  legal: boolean; // reach.legal: the selection is a legal build within 55 (export gates on it)
  buildOrder: BuildStep[] | null; // live (tries=32) oracle-verified order to assemble the selection, or null (verified or absent)
  buildOrderStates: StepState[] | null; // per-step post-states from the verifying replay; present exactly when buildOrder is
  /** Compare mode: the verified baseline-to-current transition, with its replay's states; null
   *  when not comparing or when no rung produced a verified order (the panel then falls back to
   *  the from-scratch order). */
  transition: { steps: TransStep[]; states: StepState[]; rung: TransitionRung } | null;
}

/**
 * THE per-click engine port. Bundles every reachability call a refresh makes (the validity-floor binary
 * search plus the dimming sweep) into one pure function, so the UI is a thin caller and tests/perf harnesses
 * can exercise the exact same work headlessly. This is the function to optimize: its cost IS the per-click
 * cost the user pays. The sweep budget is raised to the floor, mirroring the controller (the cap can never
 * sit below the fewest points that keep the selection legal). Requires dimming on (finite cap, present
 * table); the uncapped/no-table path is permissive and cheap, handled in the adapter.
 */
export function selectionView(
  model: DevotionModel,
  cons: ReachCon[],
  table: CoverTable,
  selected: Set<StarId>,
  cap = BUDGET,
  baseline: Set<StarId> | null = null,
): SelectionView {
  const minCost = selectionMinCost(model, cons, table, selected);
  const reach = reachabilityForSelection(model, cons, table, selected, Math.max(cap, minCost));
  const members = selectionSummary(model, selected).built;
  // Compare mode: the transition REPLACES the from-scratch computation (roughly flat per-click
  // cost); a none pair falls back to the from-scratch order so compare never shows less than today.
  if (baseline && baseline.size > 0) {
    const baseMembers = selectionSummary(model, baseline).built;
    const raw = transitionOrderPath(cons, table, baseMembers, members, cap, 16);
    const gated = raw ? gateTransition(cons, baseMembers, members, raw.steps, cap) : null;
    if (gated) {
      return {
        minCost,
        reach,
        legal: reach.legal,
        buildOrder: null,
        buildOrderStates: null,
        transition: { steps: gated.steps, states: gated.states, rung: raw!.rung },
      };
    }
  }
  // Verified or absent: render only orders the independent oracle proves legal at every step;
  // anything else is withheld and the panel shows its honest empty state instead. The verifying
  // replay's per-step states ride along for the step popup - one walk, two outputs.
  const raw = members.length ? buildOrderPath(cons, table, members, cap, 32) : null;
  const gated = gateBuildOrder(cons, members, raw, cap);
  return {
    minCost,
    reach,
    legal: reach.legal,
    buildOrder: gated?.steps ?? null,
    buildOrderStates: gated?.states ?? null,
    transition: null,
  };
}
